import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function WriteStory({ onSuccess, user }) {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [author, setAuthor] = useState('');
  const [genre, setGenre] = useState('');
  const [genres, setGenres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(false);
  const [error, setError] = useState(null);
  const [submitToCompetition, setSubmitToCompetition] = useState(true);

  useEffect(() => {
    fetchGenres();
    // If user is logged in, set default author to their name
    if (user) {
      const displayName = user.pen_name || user.pet_name || user.display_name || 'Anonymous';
      setAuthor(displayName);
    }
  }, [user]);

  const fetchGenres = async () => {
    try {
      console.log('Fetching genres...');
      const response = await axios.get(`${API_URL}/genres`);
      console.log('Genres response:', response.data);

      if (response.data.success && Array.isArray(response.data.data)) {
        setGenres(response.data.data);
        if (response.data.data.length > 0) {
          setGenre(response.data.data[0].name); // Set default genre
        }
      } else {
        console.error('Unexpected response format:', response.data);
        // Fallback genres
        setGenres([
          { name: 'Fantasy' },
          { name: 'Science Fiction' },
          { name: 'Romance' },
          { name: 'Mystery' },
          { name: 'Horror' }
        ]);
      }
    } catch (error) {
      console.error('Error fetching genres:', error);
      // Show a more informative error so the real cause is visible
      const url = `${API_URL}/genres`;
      const status = error.response ? `HTTP ${error.response.status}` : (error.message || 'Network error');
      const backendHint = error.response
        ? ''
        : ' — Check the backend is running on http://localhost:5001 (see /tmp/backend.log).';
      setError(`Failed to load genres from ${url} (${status}). Using default list.${backendHint}`);
      // Fallback genres if API fails
      setGenres([
        { name: 'Fantasy' },
        { name: 'Science Fiction' },
        { name: 'Romance' },
        { name: 'Mystery' },
        { name: 'Horror' }
      ]);
    }
  };

  // Get the display name for the logged-in user
  const getUserDisplayName = () => {
    if (!user) return '';
    return user.pen_name || user.pet_name || user.display_name || 'Anonymous';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !content) {
      alert('Please fill in title and content');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/stories`, {
        title,
        content: content.replace(/\n/g, '<br/>'),
        author: author || 'Anonymous',
        genre: genre || 'Uncategorized',
        submitToCompetition: user && submitToCompetition
      }, {
        withCredentials: true
      });

      if (response.data.success) {
        // New pipeline: story is queued for review (smut check -> summary -> image -> publish)
        const queueId = response.data.queue_id || response.data.data?.queue_id;
        let message = '✅ Story submitted for review!\n\n';
        message += 'Your story has been queued and will be reviewed by our AI moderator. ';
        message += 'Once approved, a summary and cover image will be generated automatically. ';
        message += 'It usually appears on the site within an hour.\n\n';
        if (queueId) message += `Queue ID: ${queueId}\n`;
        if (response.data.competition_entry_id) {
          message += '🏆 Your story has been entered into the competition!';
        }
        alert(message);
        // Reset form
        setTitle('');
        setContent('');
        setAuthor(user ? getUserDisplayName() : '');
        setGenre(genres[0]?.name || '');
        if (onSuccess) onSuccess();
      }
    } catch (error) {
      console.error('Error publishing story:', error);
      alert('Error publishing story: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="write-story">
      <h2>✍️ Write Your Story</h2>

      {error && <div className="error-message">{error}</div>}

      <div className="write-form">
        <div className="form-group">
          <label>Story Title *</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter a catchy title..."
            maxLength="200"
            required
          />
        </div>

        <div className="form-group">
          <label>Author Name</label>
          {user ? (
            // Logged-in user: show dropdown with Anonymous and their name
            <select
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              className="author-select"
            >
              <option value="Anonymous">Anonymous</option>
              <option value={getUserDisplayName()}>{getUserDisplayName()}</option>
            </select>
          ) : (
            // Not logged in: show free text input
            <input
              type="text"
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Your name (optional)"
            />
          )}
          {user && (
            <p className="hint" style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
              Logged in as <strong>{user.email}</strong>. Choose "Anonymous" to hide your identity, or use your display name to enter the competition.
            </p>
          )}
        </div>

        <div className="form-group">
          <label>Genre *</label>
          <select value={genre} onChange={(e) => setGenre(e.target.value)} required>
            <option value="">Select a genre...</option>
            {genres.map((g, index) => (
              <option key={index} value={g.name}>
                {g.name} {g.story_count !== undefined ? `(${g.story_count} stories)` : ''}
              </option>
            ))}
          </select>
          {genres.length === 0 && (
            <p className="hint">Loading genres... If this takes too long, check if backend is running.</p>
          )}
        </div>

        {user && (
          <div className="form-group">
            <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="checkbox"
                checked={submitToCompetition}
                onChange={(e) => setSubmitToCompetition(e.target.checked)}
                style={{ width: 'auto' }}
              />
              <span>🏆 Enter this story into the {new Date().getFullYear()} competition</span>
            </label>
            <p className="hint" style={{ fontSize: '0.85em', color: '#666', marginTop: '4px' }}>
              Only logged-in users can participate. Your story will be ranked based on views (1 pt each) and likes (5 pts each).
            </p>
          </div>
        )}

        <div className="form-group">
          <label>Story Content *</label>
          <div className="editor-toolbar">
            <button type="button" onClick={() => setPreview(!preview)}>
              {preview ? '✏️ Edit' : '👁️ Preview'}
            </button>
          </div>

          {!preview ? (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Write your story here... Use line breaks for paragraphs."
              rows="15"
              required
            />
          ) : (
            <div className="preview-content">
              <h3>{title || 'Your Title Here'}</h3>
              <p><strong>By {author || 'Anonymous'}</strong></p>
              <p><strong>Genre: {genre || 'Not selected'}</strong></p>
              <hr/>
              <div dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br/>') }} />
            </div>
          )}
        </div>

        <div className="form-actions">
          <button onClick={handleSubmit} disabled={loading} className="publish-btn">
            {loading ? 'Publishing...' : '📖 Publish Story'}
          </button>
          <button onClick={() => {
            setTitle('');
            setContent('');
            setAuthor(user ? getUserDisplayName() : '');
          }} type="button" className="clear-btn">
            Clear
          </button>
        </div>
      </div>
    </div>
  );
}

export default WriteStory;